import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnChanges,
  Output,
  SimpleChanges,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HapticsService } from '@core';
import {
  SzButtonComponent,
  SzIconComponent,
  SzNumFocalComponent,
  SzNumpadGridComponent,
  SzNumpadKey,
  SzToggleComponent
} from '@ui';
import { RunInput } from '../../../application/state';
import { centsDigitsToSeconds, formatCentsDigits, MAX_TIME_DIGITS } from '../time-digits.util';

const QUICK_CHIPS: ReadonlyArray<number> = [10, 15, 20, 25, 30, 50] as const;
const MAX_DIGITS: number = 4;

type RepsStep = 'reps' | 'par';

@Component({
  selector: 'lib-numpad-reps',
  templateUrl: './numpad-reps.component.html',
  styleUrls: ['./numpad-reps.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    SzNumpadGridComponent,
    SzNumFocalComponent,
    SzButtonComponent,
    SzIconComponent,
    SzToggleComponent
  ]
})
export class NumpadRepsComponent implements OnChanges {
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly haptics: HapticsService = inject(HapticsService);

  readonly initialPayload: InputSignal<RunInput | null> = input<RunInput | null>(null);
  readonly resetTrigger: InputSignal<number> = input<number>(0);

  @Output() readonly submitRun: EventEmitter<RunInput> = new EventEmitter<RunInput>();
  @Output() readonly cancel: EventEmitter<void> = new EventEmitter<void>();

  readonly quickChips: ReadonlyArray<number> = QUICK_CHIPS;

  readonly step: WritableSignal<RepsStep> = signal<RepsStep>('reps');
  readonly digits: WritableSignal<string> = signal<string>('');
  readonly parEnabled: WritableSignal<boolean> = signal<boolean>(false);
  readonly parTimeDigits: WritableSignal<string> = signal<string>('');

  readonly currentValue: Signal<number> = computed((): number => {
    const d: string = this.digits();
    if (d.length === 0) return 0;
    return Number.parseInt(d, 10);
  });

  readonly displayValue: Signal<string> = computed((): string => {
    const v: number = this.currentValue();
    return v === 0 ? '0' : v.toString();
  });

  readonly parTimeValue: Signal<number> = computed(() => centsDigitsToSeconds(this.parTimeDigits()));
  readonly parTimeDisplay: Signal<string> = computed(() => formatCentsDigits(this.parTimeDigits()));

  readonly canSubmitReps: Signal<boolean> = computed(() => this.currentValue() > 0);

  readonly keys: Signal<readonly SzNumpadKey[]> = computed((): readonly SzNumpadKey[] => [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
    { label: '00', value: '00', variant: 'fn' },
    { label: '0', value: '0' },
    { label: this.translate.instant('session.numpad.backspace'), value: 'del', variant: 'fn', icon: 'arrow-back' }
  ]);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialPayload'] || changes['resetTrigger']) {
      const init: RunInput | null = this.initialPayload();
      const reps: number | null = init?.reps ?? null;
      const par: number | null = init?.parTimeSeconds ?? null;
      this.digits.set(reps === null || reps === 0 ? '' : reps.toString());
      this.parTimeDigits.set(par === null ? '' : Math.round(par * 100).toString());
      this.parEnabled.set(par !== null);
      this.step.set('reps');
    }
  }

  onKeyPress(value: string): void {
    if (this.step() === 'par') {
      this.handleParKey(value);
      return;
    }
    if (value === 'del') {
      this.digits.update((cur: string) => cur.slice(0, -1));
      return;
    }
    if (value === '00') {
      if (this.digits().length + 2 > MAX_DIGITS) return;
      this.digits.update((cur: string) => (cur === '' ? '0' : cur + '00'));
      return;
    }
    this.digits.update((cur: string) => (cur.length >= MAX_DIGITS ? cur : cur + value));
  }

  pickChip(value: number): void {
    this.digits.set(value.toString());
  }

  setParEnabled(enabled: boolean): void {
    this.parEnabled.set(enabled);
    if (!enabled) this.parTimeDigits.set('');
  }

  goToPar(): void {
    if (!this.canSubmitReps()) return;
    this.haptics.impact('light');
    this.step.set('par');
  }

  goBackToReps(): void {
    this.step.set('reps');
  }

  submit(): void {
    if (!this.canSubmitReps()) return;
    this.haptics.impact('medium');
    const par: number = this.parTimeValue();
    this.submitRun.emit({
      reps: this.currentValue(),
      parTimeSeconds: this.parEnabled() && par > 0 ? par : null
    });
  }

  private handleParKey(value: string): void {
    if (value === 'del') {
      this.parTimeDigits.update((cur: string) => cur.slice(0, -1));
      return;
    }
    if (value === '00') {
      this.parTimeDigits.update((cur: string) =>
        cur.length + 2 > MAX_TIME_DIGITS ? cur + '0'.repeat(Math.max(0, MAX_TIME_DIGITS - cur.length)) : cur + '00'
      );
      return;
    }
    this.parTimeDigits.update((cur: string) => (cur.length >= MAX_TIME_DIGITS ? cur : cur + value));
  }
}
